/**
 * InlineAlert — FROZEN PUBLIC CONTRACT
 *
 * @public API (do not change without coordinating agent sign-off):
 *   @Input() tone: 'info' | 'warning' | 'danger' | 'success'
 *   @Input() message: string                          (simple text use)
 *   <ng-content />                                    (rich-content use)
 *
 * Usage:
 *   <app-inline-alert tone="warning" message="Rate data may be stale." />
 *   <app-inline-alert tone="danger">
 *     <strong>Required:</strong> Please fill in all fields.
 *   </app-inline-alert>
 *
 * Replaces Bootstrap alert-* patterns. Does NOT use Spartan HlmAlert (heavier).
 */
import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideInfo,
  lucideAlertTriangle,
  lucideXCircle,
  lucideCheckCircle,
} from '@ng-icons/lucide';

export type AlertTone = 'info' | 'warning' | 'danger' | 'success';

@Component({
  selector: 'app-inline-alert',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideIcons({ lucideInfo, lucideAlertTriangle, lucideXCircle, lucideCheckCircle })],
  imports: [NgIcon],
  template: `
    <div [class]="containerClasses()" role="alert">
      <ng-icon [name]="iconName()" class="size-4 mt-0.5 shrink-0" />
      <div class="text-sm leading-snug">
        @if (message()) {
          {{ message() }}
        } @else {
          <ng-content />
        }
      </div>
    </div>
  `,
})
export class InlineAlertComponent {
  readonly tone = input<AlertTone>('info');
  readonly message = input<string>('');

  containerClasses(): string {
    const base = 'flex items-start gap-3 rounded-lg border p-4';
    const map: Record<AlertTone, string> = {
      info:    `${base} border-blue-200 bg-blue-50 text-blue-800`,
      warning: `${base} border-amber-200 bg-amber-50 text-amber-800`,
      danger:  `${base} border-red-200 bg-red-50 text-red-800`,
      success: `${base} border-green-200 bg-green-50 text-green-800`,
    };
    return map[this.tone()];
  }

  iconName(): string {
    const map: Record<AlertTone, string> = {
      info:    'lucideInfo',
      warning: 'lucideAlertTriangle',
      danger:  'lucideXCircle',
      success: 'lucideCheckCircle',
    };
    return map[this.tone()];
  }
}
